import parsePhoneNumberFromString from 'libphonenumber-js/max';
import type { IntlShape } from 'react-intl/src/types';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import type { Country, Locale } from '@/types/regionTypes';

import type { UserFragment } from '@/graphql/saleor/api';
import { LocaleConfiguration } from '@/lib/configuration/configuration';
import { EMAIL_REGX, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';

import type { PhonePrefix } from '../DeliveryView/helpers';

export type NewsLetterSchema = InferType<
  ReturnType<typeof newsLetterSchema>
> & { reCaptchaResponse: string };

export const titleOptions = ['Mr', 'Mrs', 'Ms'].map((item) => ({
  label: item,
  value: item,
}));

export const getDefaultLanguageOption = (currVal: string) => {
  const languages = LocaleConfiguration.getInstance().getLanguages();
  const languageOptions = Object.values(languages).map((value) => ({
    value: value.sfCode,
    label: value.name,
  }));

  return languageOptions.filter((item) => item.value === currVal)[0];
};
export const getDefaultCountryOption = (currVal: string) => {
  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();
  const countryOptions = supportedCountries.map((value) => ({
    value: value.code,
    label: value.name,
  }));
  return countryOptions.filter((item) => item.value === currVal)[0];
};

export const newsLetterSchema = (
  formatMessage: IntlShape['formatMessage'],
  country: Country,
  locale: Locale,
  user: Partial<UserFragment> | null,
  isFormFieldDisabled = false,
  titleDefaultValue?: string,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const invalidPhoneMsg = formatMessage(formMessages.invalidPhone);

  let _title = null;
  let _newsletterConsent = false;
  if (user && user.metadata) {
    _title = getMetaValue(user.metadata, ACCOUNT_METADATA.personalTitle);
    const __newsletterConsent =
      getMetaValue(user.metadata, ACCOUNT_METADATA.newsletterConsent) ??
      'false';
    _newsletterConsent = ['true', true]?.includes(__newsletterConsent);
  }

  return object({
    title: getTitleSchema(
      formatMessage,
      country.code,
      titleDefaultValue ?? _title,
    ),
    firstName: getNameSchema(formatMessage, 'first', user?.firstName),
    lastName: getNameSchema(formatMessage, 'last', user?.lastName),
    ...(country.code === 'JP' && {
      firstNameKanji: getNameSchema(
        formatMessage,
        'firstKanji',
        getMetaValue(user?.metadata ?? [], ACCOUNT_METADATA.first_name_kanji) ??
          '',
        isFormFieldDisabled, //Compatible with old data, if isFormFieldDisabled set this optional
      ),
      lastNameKanji: getNameSchema(
        formatMessage,
        'lastKanji',
        getMetaValue(user?.metadata ?? [], ACCOUNT_METADATA.last_name_kanji) ??
          '',
        isFormFieldDisabled, //Compatible with old data, if isFormFieldDisabled set this optional
      ),
    }),
    phonePrefix: object({
      value: string(),
      label: string(),
      code: string(),
    }).default(getPhonePrefixSelectOptByCode(country.code)),
    phone: string()
      .max(
        MAX_CHAR_PHONE,
        formatMessage(formMessages.maxChars, {
          max: MAX_CHAR_PHONE,
        }),
      )
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix,
        then: (schema) =>
          schema.test('is-valid-phone', invalidPhoneMsg, function (value) {
            if (!value) return true; //not required, if is empty,pass
            const { phonePrefix } = this.parent;
            const fullPhoneNumber = `${phonePrefix.value}${value}`;
            const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
            return phoneNumber ? phoneNumber.isValid() : false;
          }),
        otherwise: () => string(),
      })
      .default(''),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(user?.email || ''),
    language: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(getDefaultLanguageOption(locale.sfCode)),
    country: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(getDefaultCountryOption(country.code)),
    newsletterConsent: bool()
      .default(_newsletterConsent)
      .oneOf([true], requiredMsg),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
